// The energy a capture gives, on its way from the square of the taken piece
// to the pips. The flow gives the energy with the move. The narrator holds it
// back when it queues the beat of the event, and the coins leave the square
// when that beat starts. Each coin gives one energy to the views when it
// drops. `rollShown.ts` reads how much is still held. Garrett, 2026-10-15:
// "it should animate the energy going from the defeated piece zone to the
// energy pill when its won from defeating piece".

import { REDUCED_MOTION_QUERY } from '../constants';
import type { Side } from '../engine/types';
import type { EventRecord } from '../game/state';
import { createStore } from '../lib/store';
import { type EnergyFlight, planEnergyDelivery } from '../motion/energyFlight';

export type Haul = {
  /** The serial of the event that gave the energy. */
  serial: number;
  side: Side;
  /** The square the coins leave from. */
  square: number;
  count: number;
  /** Empty until the beat starts, and with reduced motion. */
  flights: readonly EnergyFlight[];
  /** The coins that left the square. */
  left: number;
  /** The coins that dropped into a pip. The views show that much of the energy. */
  landed: number;
};

export const haulStore = createStore<readonly Haul[]>([]);

let timers: ReturnType<typeof setTimeout>[] = [];

const update = (serial: number, change: (haul: Haul) => Haul): void =>
  haulStore.set((hauls) => hauls.map((haul) => (haul.serial === serial ? change(haul) : haul)));

/** Forgets every haul, for a new match or a loaded one. */
export const clearHauls = (): void => {
  for (const timer of timers) clearTimeout(timer);
  timers = [];
  haulStore.set([]);
};

/** Holds back the energy of the event until its beat starts. An event with no energy does nothing. */
export const holdHaul = (event: EventRecord): void => {
  const { energy } = event;
  const square = event.squares[0];
  if (!energy || energy.count <= 0 || square === undefined) return;
  haulStore.set((hauls) => [
    ...hauls,
    {
      serial: event.serial,
      side: energy.side,
      square,
      count: energy.count,
      flights: [],
      left: 0,
      landed: 0,
    },
  ]);
};

/** Sends the coins of the event, at the start of its beat. With reduced motion the pips fill at once. */
export const startHaul = (serial: number): void => {
  const haul = haulStore.get().find((candidate) => candidate.serial === serial);
  if (!haul) return;
  const flies = !window.matchMedia(REDUCED_MOTION_QUERY).matches;
  if (!flies) {
    haulStore.set((hauls) => hauls.filter((current) => current.serial !== serial));
    return;
  }
  // The plan draws from `Math.random`, never from the match source, as the carry does.
  const { flights } = planEnergyDelivery(haul.count, true, Math.random);
  update(serial, (current) => ({ ...current, flights }));
  for (const flight of flights) {
    timers.push(
      setTimeout(
        () => update(serial, (current) => ({ ...current, left: current.left + 1 })),
        flight.leaveMs,
      ),
      setTimeout(() => {
        update(serial, (current) => ({ ...current, landed: current.landed + 1 }));
        // A haul whose coins all dropped is done.
        haulStore.set((hauls) => hauls.filter((current) => current.landed < current.count));
      }, flight.landMs),
    );
  }
};

/** The energy of the side that a capture gave and no coin brought to the pips yet. */
export const heldHaul = (side: Side): number =>
  haulStore
    .get()
    .filter((haul) => haul.side === side)
    .reduce((sum, haul) => sum + haul.count - haul.landed, 0);
